<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2022 (original work) Open Assessment Technologies SA;
    import { generateElementId, __, getActualKey } from '@oat-sa-private/ui-core';
    import Loading from '../loading/Loading.svelte';
    import { Icon } from '@oat-sa-private/ui-elements';
    import { allFeedbacks } from '@oat-sa-private/ui-elements/feedback';
    import validationFactory from '@oat-sa-private/ui-elements/input/inputValidation';
    import { createEventDispatcher, tick, onMount, onDestroy } from 'svelte';
    import { filterGroups, filterOptions, flattenOptions, pipeline, isEmpty } from './utils.js';

    const dispatch = createEventDispatcher();

    let listboxId = generateElementId('listbox');
    let optionIdPrefix = generateElementId('option');

    /**
     * @typedef {Object} OptionObject
     * @property {boolean} [$isDisabled] - specifies if option is disabled
     */

    /**
     * @typedef {Object} FeedbackProps
     * @property {boolean} showValidFeedback
     * @property {boolean} showInvalidFeedback
     */

    /**
     * @property {boolean} [enableInternalSearch=true] - Filter the options array based on search query
     * @property {Array[OptionObject|string|number]} options - Array of available options
     * @property {boolean} [multiple=false] - Equivalent to the `multiple` attribute on a native `<select>` input.
     * @property {Array[OptionObject]|OptionObject|string|number} [value=[]]
     * @property {string} [trackBy] - Key to compare option objects
     * @property {string|(option: OptionObject|string|number) => string} [optionLabel] - Label key in OptionObject or function to retreive label from option
     * @property {boolean} [searchable=true] - Enable/disable search in options
     * @property {string} [placeholder='Select option'] - Equivalent to the `placeholder` attribute on a native `<select>` input
     * @property {boolean} [allowEmpty=true] - Allow to remove all selected values
     * @property {boolean} [required=false] Corresponds to native HTML `required` attribute
     * @property {boolean} [closeOnSelect=true] - Enables closing after selecting an option
     * @property {boolean|number} [selectionLimit=false] - Number of allowed selected options. No limit if 0 or false.
     * @property {boolean|number} [displayOptionsLimit=false] - Limits the options displayed in the dropdown to first N options. Disables limit if 0 or false.
     * @property {string} [groupValues] - Name of the property containing group values
     * @property {string} [groupLabel] - Name of the property containing group label
     * @property {string} [allowGroupSelect=false] - Allow to select all group values by selecting the group label
     * @property {boolean} [loading=false] - Sets loading state
     * @property {boolean} [disabled=false] - Disables the select if true
     * @property {number} [tabindex=0]
     * @property {number} [size] - Dropdown size (equivalent to `size` for `<input />`)
     * @property {'small'|'medium'|'large'} [height='medium'] - Sets control and option height
     * @property {boolean} [fullwidth=false] - Determines if element should display as block-level and fill its parent
     * @property {string} [noOptionsMessage='List is empty'] - Message for situations when list of options is empty
     * @property {string} [noResultsMessage='No results found.'] - Message for situations when there are no items in the list conforming the search query
     * @property {string} [feedback=''] Selects a feedback subcomponent from an internal list. Possible values: `inline`
     * @property {FeedbackProps} [feedbackProps={}] Configuration properties for the integrated InlineFeedback component
     * @property {string} feedbackLang Language code for feedback `lang` property
     * @property {Object|CustomValidity} [customValidity={valid: true}] - Validity object for invalidating an otherwise valid Input from the outside.
     * @property {boolean} [visited=false] Set the field as if it was already visited. Usually used to restore the input with a value. The validation will trigger accordingly
     * @property {string} [openDirection=''] Set fixed opening direction
     * @fires change {CustomEvent} value change event, details contains new value
     * @fires validation {CustomEvent} fired when validation has performed with validity status in details
     */
    export let enableInternalSearch = true;
    export let options = [];
    export let multiple = false;
    export let value = [];
    export let trackBy;
    export let optionLabel;
    export let searchable = true;
    export let placeholder = __('Select option');
    export let allowEmpty = true;
    export let closeOnSelect = true;
    export let selectionLimit = false;
    export let displayOptionsLimit = false;
    export let groupValues;
    export let groupLabel;
    export let allowGroupSelect = false;
    export let loading = false;
    export let disabled = false;
    export let tabindex = 0;
    export let size;
    export let height = 'medium';
    export let fullwidth = false;
    export let noOptionsMessage = __('List is empty');
    export let noResultsMessage = __('No results found.');
    export let openDirection = '';

    // props for dynamic feedback
    export let feedback = '';
    export let required = false;
    export let feedbackProps = {};
    export let feedbackLang;
    export let customValidity = { valid: true };
    export let visited = false; // true after first focus

    const heights = ['small', 'medium', 'large'];
    if (!heights.includes(height)) {
        height = 'medium';
    }

    let search = '';
    let isOpen = false;
    let pointer = -1;

    // DOM elements
    let labelElement = null;
    let rootElement = null;
    let searchElement = null;
    let contentWrapperElement = null;
    let listElement = null;
    let afterListElement = null;

    let validity = getValidity();
    let firstTry = !visited; // false after first blur
    let focus = false;
    let preferredOpenDirection = 'below';

    // re-validate when new customValidity received
    $: if (searchElement && customValidity) {
        validity = getValidity();
    }
    $: internalValue = computeInternalValue(value);
    $: filteredOptions = computeFilteredOptions(
        search,
        options,
        enableInternalSearch,
        groupValues,
        groupLabel,
        getOptionLabel,
        displayOptionsLimit
    );
    $: valueKeys = computeValueKeys(trackBy, internalValue);
    $: currentOptionLabel = computeCurrentOptionLabel(multiple, searchable, placeholder, internalValue, getOptionLabel);
    $: isSingleLabelVisible = (singleValue || singleValue === 0) && (!isOpen || !searchable);
    $: isPlaceholderVisible = !internalValue.length && (!searchable || !isOpen);
    $: singleValue = internalValue[0];
    $: inputStyle = computeInputStyle(searchable, multiple, value, isOpen);
    $: optionHighlightClasses = computeOptionHighlightClasses(filteredOptions, pointer, value);
    $: listSize = size
        ? parseInt(size, 10)
        : Math.max(...filteredOptions.map(option => getOptionLabel(option).length), placeholder.length) + 3;
    $: controlHeight = {
        small: 'var(--space-4x)',
        medium: 'var(--space-6x)',
        large: 'var(--space-8x)'
    }[height];
    $: isAbove = computeIsAbove(openDirection, preferredOpenDirection);
    $: dispatch('search', search);

    function checkIfActiveElementIsInAfterListContent() {
        if (!afterListElement) {
            return false;
        }
        return afterListElement.contains(document.activeElement);
    }

    /**
     * Update internal search value
     * @param {InputEvent} evt - input event
     */
    function updateSearch(evt) {
        search = evt.target.value;
    }

    /**
     * Find out if given option is included in result value
     * @param {OptionObject|string|number} option
     * @returns {boolean}
     */
    function checkIfOptionIsSelected(option) {
        const opt = trackBy ? option[trackBy] : option;
        return valueKeys.includes(opt);
    }

    /**
     * Finds out if the given option is disabled
     * @param {OptionObject|string|number} option
     * @returns {boolean}
     */
    function checkIfOptionDisabled(option) {
        return !!option.$isDisabled;
    }

    /**
     * Get option label
     * @param {OptionObject|string|number} option
     * @returns {string}
     */
    function getOptionLabel(option) {
        if (isEmpty(option)) {
            return '';
        }
        if (option.$isLabel) {
            return option.$groupLabel;
        }
        let customOptionLabel = '';
        if (typeof optionLabel === 'function') {
            customOptionLabel = optionLabel(option);
        } else {
            customOptionLabel = optionLabel ? option[optionLabel] : option;
        }
        return isEmpty(customOptionLabel) ? '' : customOptionLabel;
    }

    /**
     * Select an option
     * If option is already selected - deselect it
     * If option is a group and selecting option groups is allowed - select all options
     * in a group
     * @param {OptionObject|string|number} option
     */
    function select(option) {
        if (option.$isLabel && allowGroupSelect) {
            selectGroup(option);
            return;
        }

        if (disabled || option.$isDisabled || option.$isLabel) {
            return;
        }

        const isSelected = checkIfOptionIsSelected(option);

        if (selectionLimit && multiple && internalValue.length === selectionLimit && !isSelected) {
            return;
        }

        if (isSelected) {
            removeElement(option);
            return;
        }

        if (multiple) {
            value = [...internalValue, option];
            dispatch('input', [...internalValue, option]);
            dispatch('change', [...internalValue, option]);
        } else {
            value = option;
            dispatch('input', option);
            dispatch('change', option);
        }
        handleValidation();
        if (closeOnSelect) {
            deactivate();
        }
    }

    /**
     * Select group of options
     * @param {OptionObject|string|number} selectedGroup
     */
    function selectGroup(selectedGroup) {
        const group = options.find(option => option[groupLabel] === selectedGroup.$groupLabel);

        if (!group) {
            return;
        }

        if (checkIfWholeGroupSelected(group)) {
            const newValue = internalValue.filter(option => !group[groupValues].includes(option));
            value = newValue;
            dispatch('input', value);
            dispatch('change', value);
        } else if (Array.isArray(group[groupValues])) {
            const optionsToAdd = group[groupValues].filter(
                option => !(checkIfOptionDisabled(option) || checkIfOptionIsSelected(option))
            );
            value = [...internalValue, ...optionsToAdd];
            dispatch('input', value);
            dispatch('change', value);
        }
        handleValidation();
        if (closeOnSelect) {
            deactivate();
        }
    }

    /**
     * Check if all options in a group been selected
     * @param {OptionObject|string|number} group
     * @returns {boolean}
     */
    function checkIfWholeGroupSelected(group) {
        if (Array.isArray(group[groupValues])) {
            return group[groupValues].every(option => checkIfOptionIsSelected(option) || checkIfOptionDisabled(option));
        }
        return false;
    }

    /**
     * Check if all options in a group been disabled
     * @param {OptionObject|string|number} group
     * @returns {boolean}
     */
    function checkIfWholeGroupDisabled(group) {
        if (Array.isArray(group[groupValues])) {
            return group[groupValues].every(checkIfOptionDisabled);
        }
        return false;
    }

    /**
     * Deselect option/group
     * @param {OptionObject|string|number} option
     */
    function removeElement(option) {
        if (disabled) {
            return;
        }
        if (option.$isDisabled) {
            return;
        }
        if (!allowEmpty && internalValue.length <= 1) {
            deactivate();
            return;
        }

        const index = typeof option === 'object' ? valueKeys.indexOf(option[trackBy]) : valueKeys.indexOf(option);

        if (multiple) {
            const newValue = internalValue.filter((item, itemIndex) => index !== itemIndex);
            value = newValue;
            dispatch('input', value);
            dispatch('change', value);
        } else {
            value = null;
            dispatch('input', value);
            dispatch('change', value);
        }
        handleValidation();
        if (closeOnSelect) {
            deactivate();
        }
    }

    /**
     * Activate dropdown if it's not active and not disabled
     */
    async function activate() {
        if (isOpen || disabled) {
            return;
        }
        isOpen = true;
        await tick();
        adjustPosition();

        if (searchable) {
            search = '';
            await tick();
            if (searchElement) {
                searchElement.focus();
            }
        } else {
            rootElement.focus();
        }
    }

    /**
     * Close dropdown if it's active
     */
    async function deactivate() {
        if (!isOpen) {
            return;
        }
        await new Promise(resolve => setTimeout(resolve, 0));
        if (checkIfActiveElementIsInAfterListContent()) {
            const blurListener = async () => {
                await new Promise(resolve => setTimeout(resolve, 0));
                if (document.activeElement !== searchElement && document.activeElement !== rootElement) {
                    deactivate();
                }
                document.activeElement.removeEventListener('blur', blurListener);
            };
            document.activeElement.addEventListener('blur', blurListener);
            return;
        }
        isOpen = false;

        if (searchable && searchElement) {
            searchElement.blur();
        }
        search = '';
    }

    /**
     * Open/close dropdown
     */
    function toggle() {
        if (isOpen) {
            deactivate();
        } else {
            activate();
        }
    }

    /**
     * Adjust dropdown position
     */
    function adjustPosition() {
        const { top: spaceAbove } = rootElement.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rootElement.getBoundingClientRect().bottom;
        const hasEnoughSpaceBelow = spaceBelow > contentWrapperElement.clientHeight;

        if (hasEnoughSpaceBelow || spaceBelow > spaceAbove || openDirection === 'below') {
            preferredOpenDirection = 'below';
        } else {
            preferredOpenDirection = 'above';
        }
    }

    /**
     * Get highlight classes for non-group options
     * @param {number} index - option index
     * @param {OptionObject|string|number} option - option object
     * @param {number} pointerValue - current pointer value
     * @returns {string}
     */
    function getOptionHighlightClasses(index, option, pointerValue) {
        let classList = [];

        if (index === pointerValue) {
            classList.push('highlighted');
        }
        if (checkIfOptionIsSelected(option)) {
            classList.push('selected');
        }

        return classList.join(' ');
    }

    /**
     * Get highlight classes for group options
     * @param {number} index - option index
     * @param {OptionObject|string|number} groupOption - option object
     * @param {number} pointerValue - current pointer value
     * @returns {string}
     */
    function getGroupHighlightClasses(index, groupOption, pointerValue) {
        let classList = [];
        if (!allowGroupSelect) {
            classList.push('disabled');
            if (groupOption.$isLabel) {
                classList.push('optgroup');
            }
            return classList.join(' ');
        }

        const group = options.find(option => option[groupLabel] === groupOption.$groupLabel);

        if (group && !checkIfWholeGroupDisabled(group)) {
            classList.push('optgroup');
            if (index === pointerValue) {
                classList.push('highlighted');
            }
            if (checkIfWholeGroupSelected(group)) {
                classList.push('selected');
            }
        } else {
            classList.push('disabled');
        }

        return classList.join(' ');
    }

    /**
     * Move pointer to next option if it is present
     * Skip disabled options and option groups if allowGroupSelect is false
     */
    async function pointerForward() {
        if (pointer < filteredOptions.length - 1) {
            pointer += 1;
        }
        if (
            (filteredOptions[pointer] && filteredOptions[pointer].$isLabel && !allowGroupSelect) ||
            filteredOptions[pointer].$isDisabled
        ) {
            // advance pointer if there's next option, and return pointer back if there are no such option
            if (pointer + 1 <= filteredOptions.length - 1) {
                pointerForward();
            } else {
                pointerBackward();
            }
            return;
        }
        await tick();
        adjustListScrollPosition();
    }

    /**
     * Move pointer to previous option if it is present
     * Skip disabled options and option groups if allowGroupSelect is false
     */
    async function pointerBackward() {
        if (pointer > 0) {
            pointer -= 1;
        }
        if (
            (filteredOptions[pointer] && filteredOptions[pointer].$isLabel && !allowGroupSelect) ||
            filteredOptions[pointer].$isDisabled
        ) {
            if (pointer - 1 >= 0) {
                pointerBackward();
            } else {
                pointerForward();
            }
            return;
        }
        await tick();
        adjustListScrollPosition();
    }

    /**
     * Adjust options list scroll position to currently highlighted option
     */
    function adjustListScrollPosition() {
        const highlightedOption = listElement.querySelector('.highlighted');
        const scrollBottom = listElement.clientHeight + listElement.scrollTop;
        const highlightedOptionBottom = highlightedOption.offsetTop + highlightedOption.clientHeight;
        if (highlightedOptionBottom > scrollBottom) {
            listElement.scrollTop = highlightedOptionBottom - listElement.clientHeight;
        } else if (highlightedOption.offsetTop < listElement.scrollTop) {
            listElement.scrollTop = highlightedOption.offsetTop;
        }
    }

    /**
     * Set pointer to specific option
     * @param {number} index - option index
     */
    function setPointer(index) {
        pointer = index;
    }

    /**
     * Handle ESC key from keydown event
     * @param {KeyboardEvent} evt
     */
    function handleEscKey(evt) {
        const key = getActualKey(evt);
        if (key === 'esc') {
            evt.preventDefault();
            deactivate();
        }
    }

    /**
     * Handle space key from keydown event
     * @param {KeyboardEvent} evt
     */
    function handleSpaceKey(evt) {
        const key = getActualKey(evt);
        if (key === 'space') {
            evt.preventDefault();
            if (isOpen) {
                if (pointer > -1) {
                    select(filteredOptions[pointer]);
                } else {
                    deactivate();
                }
            } else {
                activate();
            }
        }
    }

    /**
     * Handle enter key from keydown event
     * @param {KeyboardEvent} evt
     */
    function handleEnterKey(evt) {
        const key = getActualKey(evt);
        if (key === 'enter') {
            evt.preventDefault();
            if (isOpen) {
                select(filteredOptions[pointer]);
            }
        }
    }

    /**
     * Handle navigation keys from keydown event
     * @param {KeyboardEvent} evt
     */
    function handleNavigationKeys(evt) {
        const key = getActualKey(evt);
        if (key === 'up') {
            evt.preventDefault();
            activate();
            pointerBackward();
        }
        if (key === 'down') {
            evt.preventDefault();
            activate();
            pointerForward();
        }
        if (key === 'home') {
            evt.preventDefault();
            activate();
            setPointer(0);
        }
        if (key === 'end') {
            evt.preventDefault();
            activate();
            setPointer(filteredOptions.length - 1);
        }
    }

    function caretMouseDownHandler(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        toggle();
    }

    function rootKeydownHandler(evt) {
        if (evt.target !== rootElement) {
            return;
        }

        handleSpaceKey(evt);
        handleEnterKey(evt);
        const key = getActualKey(evt);
        if (key === 'enter') {
            evt.preventDefault();
            if (!isOpen) {
                activate();
            }
        }
        handleNavigationKeys(evt);
        handleEscKey(evt);
    }

    function searchInputKeydownHandler(evt) {
        handleEnterKey(evt);
        handleNavigationKeys(evt);
        handleEscKey(evt);
    }

    /**
     * Prevents label click event if evt.target is inside dropdown in order
     * to prevent reopening dropdown when clicking on it inside label
     * @param {MouseEvent} evt
     */
    function labelClickHandler(evt) {
        if (rootElement.contains(evt.target)) {
            evt.preventDefault();
        }
    }

    function computeInternalValue(...dependencies) {
        const [_value] = dependencies;
        if (_value || _value === 0) {
            if (Array.isArray(_value)) {
                return _value;
            }
            return [_value];
        }
        return [];
    }

    function computeFilteredOptions(...dependencies) {
        const [
            _search,
            _options,
            _enableInternalSearch,
            _groupValues,
            _groupLabel,
            _getOptionLabel,
            _displayOptionsLimit
        ] = dependencies;

        const normalizedSearch = (_search ?? '').toLowerCase().trim();

        let filteredOptionsArray = [..._options];

        if (_enableInternalSearch) {
            /* eslint-disable indent */
            filteredOptionsArray = _groupValues
                ? pipeline(
                      filterGroups(normalizedSearch, _groupValues, _groupLabel, _getOptionLabel),
                      flattenOptions(_groupValues, _groupLabel)
                  )(_options)
                : filterOptions(_options, normalizedSearch, _getOptionLabel);
            /* eslint-enable indent */
        } else {
            filteredOptionsArray = _groupValues ? flattenOptions(_groupValues, _groupLabel)(_options) : _options;
        }

        if (_displayOptionsLimit) {
            filteredOptionsArray = filteredOptionsArray.slice(0, _displayOptionsLimit);
        }

        return filteredOptionsArray;
    }

    function computeValueKeys(...dependencies) {
        const [_trackBy, _internalValue] = dependencies;
        if (_trackBy) {
            return _internalValue.map(el => el[_trackBy]);
        }
        return _internalValue;
    }

    function computeCurrentOptionLabel(...dependencies) {
        const [_multiple, _searchable, _placeholder, _internalValue] = dependencies;

        if (_multiple) {
            return `Selected ${_internalValue.length} option(s)`;
        }
        if (_internalValue.length) {
            return getOptionLabel(_internalValue[0]);
        } else if (_searchable) {
            return '';
        } else {
            return _placeholder;
        }
    }

    function computeInputStyle(...dependencies) {
        const [_searchable, _multiple, _value, _isOpen] = dependencies;

        if (_searchable || (_multiple && _value && _value.length)) {
            return _isOpen ? 'width: 100%;' : "width: 0; position: 'absolute'; padding: 0;";
        }
        return '';
    }

    function computeOptionHighlightClasses(...dependencies) {
        const [_filteredOptions, _pointer] = dependencies;
        return _filteredOptions.reduce(
            (accumulator, option, index) => ({
                ...accumulator,
                [index]:
                    option.$isLabel || option.$isDisabled
                        ? getGroupHighlightClasses(index, option, _pointer)
                        : getOptionHighlightClasses(index, option, _pointer)
            }),
            {}
        );
    }

    function computeIsAbove(...dependencies) {
        const [_openDirection, _preferredOpenDirection] = dependencies;
        if (_openDirection === 'above') {
            return true;
        } else if (_openDirection === 'below') {
            return false;
        } else {
            return _preferredOpenDirection === 'above';
        }
    }

    // Prepare validation
    const pattern = null;
    const maxlength = null;
    const minlength = null;
    const patternMessage = '';
    const validate = validationFactory({ required, maxlength, minlength, pattern, patternMessage });

    /**
     * Validate input value
     * @fires validation
     */
    function handleValidation() {
        validity = getValidity();
        dispatch('validation', { validity });
    }

    /**
     * Validate the input element and set local validity
     * @returns {Object{}} {messages=[], overallValidity=Boolean}
     */
    function getValidity() {
        return (visited || customValidity?.valid === false) && searchElement
            ? validate(searchElement, customValidity, value)
            : { messages: [], overallValidity: true };
    }

    onMount(() => {
        labelElement = rootElement.closest('label');
        labelElement?.addEventListener('click', labelClickHandler);
    });

    onDestroy(() => {
        labelElement?.removeEventListener('click', labelClickHandler);
    });
</script>

<style>
    .dropdown,
    .input,
    .single-label {
        font-family: inherit;
        font-size: 1em;
        touch-action: manipulation;
    }

    .dropdown {
        --list-height: 40rem;
        --dropdown-border-width: var(--border-thin);
        --dropdown-border-color: var(--color-border-input-default);
        --control-border-color: var(--dropdown-border-color);
        display: inline-flex;
        align-items: center;
        position: relative;
        border: var(--dropdown-border-width) solid var(--control-border-color);
        height: var(--dropdown-height);
        background-color: var(--color-bg-default);
        width: calc(var(--list-size) + 6rem);

        &:hover,
        &:active {
            --control-border-color: var(--color-border-actionable-hover);
            outline: none;
        }

        &:focus,
        &:focus-visible,
        &:focus-within {
            @add-mixin simple-outline;
        }

        &.disabled {
            --control-border-color: var(--color-border-disabled);
            color: var(--color-text-disabled);
            background-color: var(--color-bg-disabled-subtle);
        }

        &.active {
            --control-border-color: var(--color-border-actionable-hover);
            z-index: var(--layer-2);
        }
        &.invalid {
            border: 1px solid var(--color-alert);
        }

        & :global(.feedback-block) {
            max-width: var(--input-width);
        }
    }

    .control {
        display: flex;
        align-items: center;
        padding-inline-start: var(--space-2x);
        width: calc(100% - 6rem);
        cursor: pointer;
        height: 100%;
    }

    .caret {
        display: flex;
        align-items: center;
        padding-left: var(--space-2x);
        padding-right: var(--space-2x);
        background: none;
        border: none;
        color: inherit;
        cursor: pointer;
        height: 100%;
    }

    .content-wrapper {
        display: none;
        position: absolute;
        top: calc(100% + var(--dropdown-border-width));
        left: calc(-1 * var(--dropdown-border-width));
        width: calc(100% + var(--dropdown-border-width) * 2);
        max-width: calc(100% + var(--dropdown-border-width) * 2);
        border-left: var(--dropdown-border-width) solid var(--dropdown-border-color);
        border-right: var(--dropdown-border-width) solid var(--dropdown-border-color);
        border-bottom: var(--dropdown-border-width) solid var(--dropdown-border-color);
        z-index: var(--layer-2);
        background-color: var(--color-bg-default);

        &.visible {
            display: block;
        }
    }

    .above .content-wrapper {
        top: unset;
        bottom: calc(100% + var(--dropdown-border-width));
        border-bottom: unset;
        border-top: var(--dropdown-border-width) solid var(--dropdown-border-color);
    }

    .dropdown,
    .single-label,
    .search-input {
        font-size: 1em;
        font-family: inherit;
        touch-action: manipulation;
    }

    .single-label,
    .search-input {
        border: none;
        padding: 0;

        &:focus {
            outline: none;
        }
    }

    .search-input {
        min-width: 0;
        background-color: transparent;
        color: inherit;
    }

    .single-label,
    .dropdown-placeholder {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .options {
        display: flex;
        flex-direction: column;
        list-style: none;
        padding: 0;
        margin: 0;
        max-height: var(--list-height);
        overflow-y: auto;
        overflow-x: hidden;
    }

    .option {
        display: flex;
        align-items: center;
        height: var(--dropdown-height);
        padding-left: var(--space-2x);
        padding-right: var(--space-2x);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        cursor: pointer;

        &.optgroup {
            font-weight: 700;
        }

        &.highlighted {
            background-color: var(--color-bg-input-hover);
            &.selected {
                background-color: var(--color-brand);
            }
        }

        &.selected {
            background-color: var(--color-bg-actionable-hover);
            color: var(--color-text-actionable);
        }

        &.disabled {
            color: var(--color-text-disabled);
            cursor: not-allowed;
        }
    }

    .options-loading,
    .options-message,
    .after-list {
        display: block;
        padding-left: var(--space-2x);
        padding-right: var(--space-2x);
    }

    .options-loading,
    .options-message {
        padding-top: var(--space-1x5);
        padding-bottom: var(--space-1x5);
    }

    .options-message {
        color: var(--color-text-disabled);
        font-size: var(--fontsize-body-s);
        text-align: center;
    }

    .after-list :global(hr) {
        margin-top: 1px;
        margin-bottom: 1px;
        padding-top: 1px;
        padding-bottom: 1px;
    }

    .after-list-content {
        padding-bottom: calc(var(--space-1x) - 1px);
        padding-top: calc(var(--space-1x) - 1px);
    }

    .validity-icon-wrapper {
        display: flex;
        width: 5.5rem;
        align-items: center;
        justify-content: center;
        color: var(--color-border-warning);
    }

    :global(.inverted) .dropdown {
        --dropdown-border-color: var(--color-text-inverted);
        background-color: var(--color-bg-inverted);

        &:focus,
        &:focus-visible,
        &:focus-within {
            outline-color: var(--color-text-inverted);
        }

        & .content-wrapper {
            background-color: var(--color-bg-inverted);
        }

        & .option {
            &.highlighted {
                background-color: var(--color-bg-actionable-secondary-hover-inverted);
                &.selected {
                    background-color: var(--color-brand);
                }
            }
        }
    }
</style>

<div
    role="combobox"
    class="dropdown"
    class:active={isOpen}
    class:disabled
    class:invalid={!validity.overallValidity}
    class:above={isAbove}
    style={`
        --list-size: ${fullwidth ? 'calc(100% - 6rem)' : `${listSize}rem`};
        --dropdown-height: ${controlHeight};
    `}
    tabindex={searchable ? -1 : tabindex}
    aria-controls={listboxId}
    aria-expanded={isOpen}
    on:blur={searchable ? () => {} : deactivate}
    on:keydown|self={rootKeydownHandler}
    bind:this={rootElement}>
    <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
    <span class="control" role="search" on:mousedown|preventDefault={toggle}>
        {#if searchable}
            <input
                class="search-input"
                type="text"
                autocomplete="off"
                spellcheck="false"
                {placeholder}
                style={inputStyle}
                value={search}
                {disabled}
                {tabindex}
                size={listSize}
                on:input={updateSearch}
                on:focus|once={() => {
                    visited = true;
                    validity = getValidity();
                }}
                on:focus|preventDefault={activate}
                on:focus={handleValidation}
                on:blur|preventDefault={deactivate}
                on:blur|once={() => {
                    validity = getValidity();
                    firstTry = false;
                }}
                on:keydown={searchInputKeydownHandler}
                aria-controls={listboxId}
                bind:this={searchElement} />
        {/if}
        {#if isSingleLabelVisible}
            <span class="single-label">
                <slot name="singleLabel" option={singleValue}>
                    {currentOptionLabel}
                </slot>
            </span>
        {/if}
        {#if isPlaceholderVisible}
            <span class="dropdown-placeholder">
                <slot name="placeholder">
                    {placeholder}
                </slot>
            </span>
        {/if}
    </span>
    <slot name="caret">
        <span class="caret" role="presentation" on:mousedown={caretMouseDownHandler}>
            {#if loading}
                <Loading size="smallest" />
            {:else}
                <Icon name="caret-down-16" ariaHidden={true} />
            {/if}
        </span>
    </slot>
    <div
        class="content-wrapper"
        class:visible={isOpen}
        role="listbox"
        tabindex="-1"
        bind:this={contentWrapperElement}
        on:focus={activate}
        on:mousedown|preventDefault>
        <div id={listboxId} class="dropdown-content">
            <ul class="options" bind:this={listElement}>
                {#each filteredOptions as option, i (i)}
                    <!-- svelte-ignore a11y-aria-activedescendant-has-tabindex -->
                    <li
                        class="element"
                        id={`${optionIdPrefix}-${i}`}
                        role={!(option && option.$isDisabled) ? 'option' : null}
                        aria-activedescendant={pointer === i}>
                        {#if option && (option.$isLabel || option.$isDisabled)}
                            <!-- svelte-ignore a11y-no-static-element-interactions -->
                            <span
                                class={`option ${optionHighlightClasses[i]}`}
                                on:mousedown|preventDefault={() => selectGroup(option)}
                                on:mouseenter|self={() => allowGroupSelect && setPointer(i)}>
                                <span>{getOptionLabel(option)}</span>
                            </span>
                        {:else}
                            <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-static-element-interactions -->
                            <span
                                class={`option ${optionHighlightClasses[i]}`}
                                on:click|stopPropagation={() => select(option)}
                                on:mouseenter|self={() => setPointer(i)}>
                                <span>{getOptionLabel(option)}</span>
                            </span>
                        {/if}
                    </li>
                {/each}
            </ul>
            {#if filteredOptions.length === 0 && loading}
                <span class="options-loading">
                    <Loading size="small" />
                </span>
            {/if}
            {#if options.length === 0 && !search && !loading}
                <span class="options-message">
                    {noOptionsMessage}
                </span>
            {/if}
            {#if filteredOptions.length === 0 && search && !loading}
                <span class="options-message">
                    {noResultsMessage}
                </span>
            {/if}
            {#if $$slots.afterList}
                <div bind:this={afterListElement} class="after-list" on:blur={deactivate}>
                    <hr />
                    <div class="after-list-content">
                        <slot name="afterList" />
                    </div>
                </div>
            {/if}
        </div>
    </div>
</div>
{#if feedback && ((feedback !== 'block' && focus) || (feedback === 'block' && !focus)) && !disabled}
    <svelte:component
        this={allFeedbacks[feedback]}
        {validity}
        {...feedbackProps}
        lang={feedbackLang}
        emptyInput={value ? !value.length : !value}
        {firstTry} />
{/if}
